import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Loader2, BarChart2 } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { DemoNotSavedNotice } from '@/components/demo/DemoNotSavedNotice'

interface StatSummaryProps {
    formData: any
    onEdit: (comment: string) => void
    onSubmit: (comment: string) => void
    onNavigateHome: () => void
    showConfirm: boolean
    onConfirmNavigateHome: () => void
    onCancelConfirm: () => void
}

export const StatSummary = ({
    formData,
    onEdit,
    onSubmit,
    onNavigateHome,
    showConfirm,
    onConfirmNavigateHome,
    onCancelConfirm
}: StatSummaryProps) => {
    const [comment, setComment] = useState(formData.statData?.comment || '')
    const [isSubmitting, setIsSubmitting] = useState(false)

    // Update comment when formData changes
    useEffect(() => {
        if (formData.statData?.comment !== undefined) {
            setComment(formData.statData.comment || '')
        }
    }, [formData.statData?.comment])

    const handleEdit = () => {
        onEdit(comment)
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setIsSubmitting(true)

        try {
            await onSubmit(comment)
        } catch (error: any) {
            console.error('Error submitting stat:', error)
            const msg = error?.message || 'Une erreur est survenue.'
            alert(`${msg}\n\nNe rechargez pas la page — vos données sont conservées et vous pouvez réessayer.`)
            setIsSubmitting(false)
        }
    }

    const formatValue = (value: any) => {
        if (Array.isArray(value)) {
            return value.join(', ')
        }
        return value?.toString() || ''
    }

    const keyTextMap: Record<string, string> = {
        locataire: "Nom du.de la locataire",
        travailleur: "Travailleur.euse",
        collegue: "Collègue.s HF présent.e.s",
        date: "Date de l'échange",
        duree: "Durée de l'échange (min)",
        format_echange: "Format de l'échange",
        contenu_echange: "Contenu initialement prévu",
        contenu_echange_real: "Contenu effectivement abordé",
        distribution_kit_rdr: "Distribution de kit RDR",
        presence: "Présence physique",
        entree_dans_appartement: "Entrée dans l'appartement",
        corps: "Corps",
        vetements: "Vêtements",
        comportement: "Comportement",
        voisinage: "Voisinage",
        hygiene: "Hygiène et logement",
        bailleur: "Bailleur",
        securite: "Sécurité",
        sante: "Santé",
        consommation: "Consommation"
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <PageHeader title="Récapitulatif - Stat" icon={<BarChart2 size={20} />} onBack={onNavigateHome} />
            <div className="max-w-4xl mx-auto py-8 px-4">
                <div className="space-y-6 mb-8">
                    {/* Information sur la stat */}
                    <div className="bg-white rounded-lg shadow p-6">
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">Information sur la stat</h2>
                        <div className="space-y-2">
                            {Object.entries(formData.statData || {}).map(([key, value]) => {
                                if (key === 'uuid' || key === 'submit_ts' || key === 'comment') return null

                                // Skip empty values
                                if (!value || (Array.isArray(value) && value.length === 0)) return null
                                return (
                                    <div key={key}>
                                        <span className="font-medium">{keyTextMap[key] || key} :</span>{' '}
                                        {formatValue(value)}
                                    </div>
                                )
                            })}
                        </div>
                    </div>

                    {/* CVC Score */}
                    {formData.cvc ? (
                        <div className="bg-white rounded-lg shadow p-6">
                            <h2 className="text-xl font-semibold text-gray-900 mb-4">Corps, Vêtements et Comportement</h2>
                            <div className="space-y-2">
                                {Object.entries(formData.cvc).map(([key, value]) => (
                                    <div key={key}>
                                        <span className="font-medium">{keyTextMap[key] || key} :</span>{' '}
                                        {String(value)}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="bg-white rounded-lg shadow p-6">
                            <h2 className="text-xl font-semibold text-gray-900 mb-4">CVC - Hestia</h2>
                            <p>Sections non remplies.</p>
                        </div>
                    )}

                    {/* HESTIA Scores */}
                    {formData.hestia && (
                        <div className="bg-white rounded-lg shadow p-6">
                            <h2 className="text-xl font-semibold text-gray-900 mb-4">HESTIA - Risque de perte de logement</h2>
                            <div className="space-y-2">
                                {Object.entries(formData.hestia).map(([key, value]) => (
                                    <div key={key}>
                                        <span className="font-medium">{keyTextMap[key] || key} :</span>{' '}
                                        {String(value)}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {formData.hestia2 && (
                        <div className="bg-white rounded-lg shadow p-6">
                            <h2 className="text-xl font-semibold text-gray-900 mb-4">HESTIA - Risque de décès</h2>
                            <div className="space-y-2">
                                {Object.entries(formData.hestia2).map(([key, value]) => (
                                    <div key={key}>
                                        <span className="font-medium">{keyTextMap[key] || key} :</span>{' '}
                                        {String(value)}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Final form */}
                <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6">
                    <DemoNotSavedNotice />
                    <div className="flex justify-between">
                        <Button type="button" variant="outline" onClick={handleEdit}>
                            Modifier la stat
                        </Button>
                        <div className="space-x-4">
                            <Button type="button" variant="outline" onClick={onNavigateHome}>
                                Accueil
                            </Button>
                            <Button type="submit" disabled={isSubmitting}>
                                {isSubmitting ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Sauvegarde...
                                    </>
                                ) : (
                                    'Valider'
                                )}
                            </Button>
                        </div>
                    </div>
                </form>
            </div>

            {showConfirm && (
                <ConfirmDialog
                    onConfirm={onConfirmNavigateHome}
                    onCancel={onCancelConfirm}
                />
            )}
        </div>
    )
} 